import { Box, Button, Divider, Stack, Typography } from '@mui/material'
import { useMemo } from 'react'
import { EmphasizedNumber, IconButton, Select } from 'utoniq-ui'

import { Item, SkuUnit } from '@/clients/utoniq-core/schema'
import { styleSets } from '@/components/styles'
import { useForm, UseFormProps, useTranslation } from '@/core'
import { Organism, OrganismFrame } from '@/core/architecture'

export type QuantitySelectModalProps = {
  item: Partial<Item> | undefined
  skuId: string | undefined
  maxChoice: number
  maxSelectItem: number
  selectedIndex: number
  onGoBack: () => void
  onSubmit: (values: QuantitySelectValue) => Promise<void>
}

type QuantitySelectValue = {
  choiceId: string
  choiceSkuId: string | undefined
  purchaseCountChoice: number
}

const schema: UseFormProps['schema'] = o => ({
  choiceId: o.string().required(),
  choiceSkuId: o.string().optional().allow(null),
  purchaseCountChoice: o.number().required(),
})

export const QuantitySelectModal: Organism<QuantitySelectModalProps> = ({
  item,
  skuId,
  maxChoice,
  maxSelectItem,
  selectedIndex,
  onGoBack,
  onSubmit,
}) => {
  const { t } = useTranslation('firstComeFirstServedDetail')
  const { handleSubmit, setValue, watch } = useForm<QuantitySelectValue>({
    schema,
    defaultValues: {
      purchaseCountChoice: 1,
      choiceId: item?.id,
      choiceSkuId: skuId,
    },
  })

  // SKUの指定があればSKUの情報を取得
  const skuUnit = useMemo<SkuUnit | undefined | null>(
    () =>
      skuId ? item?.skuSettings?.units.find(sku => sku.id === skuId) : null,
    [skuId, item?.skuSettings]
  )
  // SKUの価格があればその価格、なければアイテムの価格を取得
  const formattedPrice = useMemo(
    () => (skuUnit?.price ? skuUnit?.formattedPrice : item?.formattedPrice),
    [skuUnit, item?.formattedPrice]
  )

  const quantity = useMemo(() => {
    if (!item?.issuedNumber) {
      return maxSelectItem
    }
    if (skuUnit?.stock) {
      const itemMaxSelectItem =
        skuUnit.stock -
        (skuUnit.pendingPurchaseNumber ?? 0) -
        (skuUnit.purchasedNumber ?? 0)
      return itemMaxSelectItem > maxSelectItem
        ? maxSelectItem
        : itemMaxSelectItem
    }
    const itemMaxSelectItem =
      item.issuedNumber -
      (item.pendingPurchaseNumber ?? 0) -
      (item.purchasedNumber ?? 0)
    return itemMaxSelectItem > maxSelectItem ? maxSelectItem : itemMaxSelectItem
  }, [
    item?.issuedNumber,
    item?.pendingPurchaseNumber,
    item?.purchasedNumber,
    maxSelectItem,
    skuUnit,
  ])

  return (
    <OrganismFrame
      sx={{
        bgcolor: 'background.default',
        height: '75dvh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack
        direction="row"
        sx={{ py: 4, px: 3, overflowY: 'auto', ...styleSets.hideScrollbar() }}
        alignItems="center"
      >
        <IconButton.Back size="small" onClick={onGoBack} />
        <Typography variant="h6" align="center" width="100%">
          {maxChoice > 1
            ? t('selectQuantityTitle', { number: selectedIndex + 1 })
            : t('selectNumberSheets', '枚数選択')}
        </Typography>
        <Box width={30} />
      </Stack>
      <Divider />
      <Typography
        variant="h5"
        width="100%"
        color="text.secondary"
        sx={{ bgcolor: 'grey.50', px: 5, py: 1 }}
      >
        {t('performanceInformation', '公演情報')}
      </Typography>
      <Typography variant="body2" width="100%" sx={{ px: 5, pt: 2 }}>
        {item?.title}
      </Typography>
      <Typography variant="body2" width="100%" sx={{ px: 5 }}>
        {skuUnit?.unitName}
      </Typography>
      <Typography
        variant="h5"
        width="100%"
        color="text.secondary"
        sx={{ bgcolor: 'grey.50', px: 5, py: 1, mt: 3 }}
      >
        {t('selectNumberSheets', '枚数選択')}
      </Typography>
      <Stack sx={{ p: 5 }}>
        <Typography variant="body2">
          {t('selectQuantityNote', { max: quantity })}
        </Typography>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ pb: 7 }}
        >
          <EmphasizedNumber variant="h2">¥{formattedPrice}</EmphasizedNumber>
          <Stack direction="row" alignItems="center" spacing={3}>
            <Select
              listItems={[...Array(quantity).keys()].map(index => ({
                value: (index + 1).toString(),
                label: (index + 1).toString(),
              }))}
              value={`${watch('purchaseCountChoice')}`}
              onChange={value => setValue('purchaseCountChoice', +value)}
              formControlProps={{
                sx: { minWidth: 100, bgcolor: 'grey.50', minHeight: 'auto' },
              }}
            />
            <Typography variant="caption">{t('sheet', '枚')}</Typography>
          </Stack>
        </Stack>
        <Stack alignItems="center" justifyContent="center" sx={{ py: 2 }}>
          <Button
            size="large"
            sx={{ width: 212 }}
            onClick={handleSubmit(onSubmit)}
          >
            {t('addNext', '次へ')}
          </Button>
        </Stack>
      </Stack>
    </OrganismFrame>
  )
}
